import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import { Progress, Button } from 'antd';
import { useTranslation } from 'react-i18next';
import { useMultichoixStepManagement } from '../hooks/useMultichoixStepManagement';
import { MULTICHOIX_SEARCH_STEPS } from '../constants/multichoixSteps';
import MultichoixStep from './multichoix/MultichoixStep';
import SearchButton from './SearchButton';
import { BackgroundSection } from '@client/feature/activeSubstance/partials';
import resetIcon from '../../../../img/reset.svg';

/**
 * Maps enrichedData field names to the step field names used in selectionLabels
 */
const ENRICHED_DATA_FIELD_MAPPING = {
  cultures: 'treatment_cultures.culture',
  culturalPractices: 'treatment_cultures.cultural_practices',
  treatmentTypes: 'treatment_type',
  enemies: 'treatment_enemies.enemy',
  interventionPeriods: 'intervention_period',
  applicationTypes: 'application_type',
};

/**
 * Merges enrichedData into selectionLabels format for cold start restoration
 * @param {Object} initialSearchData - The initial search data with selections and selectionLabels
 * @param {Object} enrichedData - The enriched data with full label information from API
 * @returns {Object} - Merged initial search data with complete selectionLabels
 */
const mergeEnrichedLabels = (initialSearchData, enrichedData) => {
  if (!initialSearchData || !enrichedData) {
    return initialSearchData;
  }

  const mergedLabels = { ...initialSearchData.selectionLabels };

  // For each enrichedData field, map to the corresponding selectionLabels field
  Object.entries(ENRICHED_DATA_FIELD_MAPPING).forEach(([enrichedKey, stepField]) => {
    const enrichedItems = enrichedData[enrichedKey];
    const existingLabels = mergedLabels[stepField];

    // Only use enriched data if we have it and existing labels are missing or empty
    if (enrichedItems && enrichedItems.length > 0 && (!existingLabels || existingLabels.length === 0)) {
      // Transform enriched items to the label format expected by the step components
      mergedLabels[stepField] = enrichedItems.map((item) => ({
        value: item.id || item.value,
        label: item.label || item.name || item.french_name || item.display_name,
        ...item, // Preserve any additional properties
      }));
    }
  });

  return {
    ...initialSearchData,
    selectionLabels: mergedLabels,
  };
};

const MultichoixSteps = ({ onSearch, onSelectionChange, initialSearchData, enrichedData }) => {
  const { t } = useTranslation('treatment-search');

  // Merge enrichedData into initialSearchData for cold start restoration
  const mergedInitialData = useMemo(() => {
    return mergeEnrichedLabels(initialSearchData, enrichedData);
  }, [initialSearchData, enrichedData]);

  const {
    currentStep,
    completedSteps,
    selections,
    selectionLabels,
    setCurrentStep,
    isStepValid,
    isStepRequired,
    areAllRequiredStepsValid,
    canAccessStep,
    getStepStatus,
    handleStepChange: internalHandleStepChange,
    handleStepSkip,
    handleStepClick,
    handleReset: internalHandleReset,
    forceStepRevalidation,
  } = useMultichoixStepManagement(mergedInitialData);

  // Wrap handleStepChange to also sync to URL
  const handleStepChange = useCallback(
    (stepKey, values, labelObjects = []) => {
      // Update internal state
      internalHandleStepChange(stepKey, values, labelObjects);

      // Sync to URL
      if (onSelectionChange) {
        const step = MULTICHOIX_SEARCH_STEPS.find((s) => s.key === stepKey);
        if (step) {
          onSelectionChange(step.field, values);

          // Clear all subsequent steps in URL (cascading reset)
          MULTICHOIX_SEARCH_STEPS.filter((s) => s.key > stepKey).forEach((s) => {
            onSelectionChange(s.field, []);
          });
        }
      }
    },
    [internalHandleStepChange, onSelectionChange]
  );

  // Wrap handleReset to also clear URL
  const handleReset = useCallback(() => {
    internalHandleReset();
    // Clear all fields in URL
    if (onSelectionChange) {
      MULTICHOIX_SEARCH_STEPS.forEach((step) => {
        onSelectionChange(step.field, []);
      });
    }
  }, [internalHandleReset, onSelectionChange]);

  const handleSearch = () => {
    const searchData = {
      selections,
      selectionLabels,
      completedSteps: completedSteps.size,
    };
    onSearch(searchData);
  };

  return (
    <div className="relative w-full min-h-screen">
      <div className="container px-4 sm:px-6 lg:px-8">
        <BackgroundSection />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex flex-col gap-6 sm:gap-8 relative z-10">
            <div className="filters-container">
              <div className="mb-6 sm:mb-8">
                <div className="flex justify-end mb-2">
                  <Button
                    size="large"
                    onClick={handleReset}
                    disabled={completedSteps.size === 0}
                    className="reset-button-with-icon"
                  >
                    <img src={resetIcon} alt="Reset" className="w-4 h-4 reset-icon" />
                    {t('actions.reset', 'Réinitialiser')}
                  </Button>
                </div>
                <h3 className="text-2xl sm:text-3xl font-medium text-black mb-4 text-center capitalize px-4">
                  {t('multichoix.title', 'Recherche multichoix')}
                </h3>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3">
                  <span className="text-sm text-gray-600">
                    {t('multichoix.progress', 'Etape {{current}} sur {{total}}', {
                      current: currentStep,
                      total: MULTICHOIX_SEARCH_STEPS.length,
                    })}
                  </span>
                  <Progress
                    percent={Math.round((completedSteps.size / MULTICHOIX_SEARCH_STEPS.length) * 100)}
                    size="small"
                    strokeColor="#5B8C00"
                    className="w-full max-w-48 sm:w-48"
                    showInfo={false}
                  />
                </div>
              </div>

              <div className="space-y-2 mb-6 sm:mb-8">
                {MULTICHOIX_SEARCH_STEPS.map((step) => {
                  const status = getStepStatus(step.key);
                  const isActive = step.key === currentStep;
                  const canClick = canAccessStep(step.key);
                  const stepSelections = selections[step.field];
                  const stepLabels = selectionLabels[step.field] || [];
                  const isCompleted = completedSteps.has(step.key);
                  const isValid = isStepValid(step.key);
                  const isRequired = isStepRequired(step.key);

                  return (
                    <MultichoixStep
                      key={step.key}
                      step={step}
                      status={status}
                      isActive={isActive}
                      canClick={canClick}
                      selections={stepSelections}
                      selectionLabels={stepLabels}
                      isCompleted={isCompleted}
                      isValid={isValid}
                      isRequired={isRequired}
                      allSelections={selections}
                      allLabels={selectionLabels}
                      onStepClick={handleStepClick}
                      onChange={handleStepChange}
                      onNext={setCurrentStep}
                      onSkip={handleStepSkip}
                      onForceRevalidation={forceStepRevalidation}
                    />
                  );
                })}
              </div>

              <div className="flex justify-center gap-4 items-center">
                <SearchButton
                  completedStepsCount={completedSteps.size}
                  areAllRequiredStepsValid={areAllRequiredStepsValid()}
                  onSearch={handleSearch}
                />
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

MultichoixSteps.propTypes = {
  onSearch: PropTypes.func.isRequired,
  onSelectionChange: PropTypes.func,
  initialSearchData: PropTypes.object,
  enrichedData: PropTypes.shape({
    cultures: PropTypes.array,
    culturalPractices: PropTypes.array,
    treatmentTypes: PropTypes.array,
    enemies: PropTypes.array,
    interventionPeriods: PropTypes.array,
    applicationTypes: PropTypes.array,
  }),
};

MultichoixSteps.defaultProps = {
  onSelectionChange: null,
  initialSearchData: null,
  enrichedData: null,
};

export default MultichoixSteps;
